import React, { useState, useEffect } from "react";
import { Chart } from "react-google-charts";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { Button, Card, Empty } from "antd";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";

function Attendance() {
  const navigate = useNavigate();
  const viewAll = () => {
    navigate("/attendance");
  };

  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("All Departments");
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchDepartments = async () => {
      setDepartmentsLoading(true);
      try {
        const response = await axiosInstance.get(API.department.getList);
        const departmentData = response.data;

        // Handle different possible response structures
        let departmentsList = [];

        if (Array.isArray(departmentData)) {
          // If it's already an array
          departmentsList = departmentData;
        } else if (departmentData && Array.isArray(departmentData.data)) {
          // If it's { data: [] } structure
          departmentsList = departmentData.data;
        } else if (departmentData && departmentData.items) {
          // If it's { items: [] } structure
          departmentsList = departmentData.items;
        } else if (departmentData && departmentData.result) {
          // If it's { result: [] } structure
          departmentsList = departmentData.result;
        }

        setDepartments(departmentsList);

        // If there are departments, select the first one by default
        if (departmentsList.length > 0) {
          const firstDept = departmentsList[0];
          const deptId = firstDept.id || firstDept.departmentId || firstDept.value;
          setSelectedDepartment(deptId);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
      } finally {
        setDepartmentsLoading(false);
      }
    };
    fetchDepartments();
  }, [apiUrl]);

  useEffect(() => {
    const fetchAttendanceData = async () => {
      if (selectedDepartment === "All Departments" || !selectedDepartment) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Call the API function with the selected department ID
        const response = await axiosInstance.get(
          API.attendance.getDepartmentAttendance(selectedDepartment)
        );
        const data = response.data;
        setAttendanceData(data);
      } catch (error) {
        console.error("Failed to fetch attendance data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (selectedDepartment !== "All Departments") {
      fetchAttendanceData();
    }
  }, [selectedDepartment, apiUrl]);

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
  };

  // Process the attendance data to get summary statistics
  const getAttendanceSummary = () => {
    if (!attendanceData || !attendanceData.records) return null;

    let totalPresent = 0;
    let totalAbsent = 0;
    let totalOnLeave = 0;
    let totalLate = 0;

    // Aggregate data from all records
    attendanceData.records.forEach((record) => {
      totalPresent += record.totalPresent || 0;
      totalAbsent += record.totalAbsent || 0;
      totalOnLeave += record.totalOnLeave || 0;
      totalLate += record.totalLate || 0;
    });

    return {
      totalPresent,
      totalAbsent,
      totalOnLeave,
      totalLate,
    };
  };

  const attendanceSummary = getAttendanceSummary();

  // Prepare chart data based on the summary
  const chartData = [
    ["Status", "Count"],
    ["Present", attendanceSummary?.totalPresent || 0],
    ["Late", attendanceSummary?.totalLate || 0],
    ["Absent", attendanceSummary?.totalAbsent || 0],
    ["On Leave", attendanceSummary?.totalOnLeave || 0],
  ];

  // Check if there's any data to display
  const hasData =
    attendanceSummary &&
    (attendanceSummary.totalPresent > 0 ||
      attendanceSummary.totalAbsent > 0 ||
      attendanceSummary.totalOnLeave > 0 ||
      attendanceSummary.totalLate > 0);

  // Get department name for display
  const getDepartmentName = () => {
    if (selectedDepartment === "All Departments") return "All Departments";
    const dept = departments.find(
      (d) =>
        d.id === selectedDepartment ||
        d.departmentId === selectedDepartment ||
        d.value === selectedDepartment
    );
    return dept
      ? dept.department || dept.name || dept.title || "Unknown Department"
      : "Selected Department";
  };

  // Get department display name for dropdown
  const getDepartmentDisplayName = (dept) => {
    return (
      dept.department || dept.name || dept.title || dept.departmentName || "Unknown Department"
    );
  };

  // Get department ID for value
  const getDepartmentId = (dept) => {
    return dept.id || dept.departmentId || dept.value;
  };

  return (
    <Card title={`Attendance - ${getDepartmentName()}`} className="card-professional">
      <div className="row">
        <div style={{ width: "100%", maxWidth: "900px", height: "300px", textAlign: "center" }}>
          {departmentsLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Oval
                height={40}
                width={40}
                color="#4fa94d"
                wrapperStyle={{}}
                wrapperClass=""
                visible={true}
                ariaLabel="oval-loading"
                secondaryColor="#4fa94d"
                strokeWidth={2}
                strokeWidthSecondary={2}
              />
            </div>
          ) : (
            <>
              <div className="col-md-12 d-flex justify-content-end">
                <div className="col-md-3 mb-3">
                  <select
                    className="form-select custom-select"
                    value={selectedDepartment}
                    onChange={handleDepartmentChange}
                  >
                    <option value="All Departments">All Departments</option>
                    {departments.map((department) => (
                      <option key={getDepartmentId(department)} value={getDepartmentId(department)}>
                        {getDepartmentDisplayName(department)}
                      </option>
                    ))}
                  </select>
                </div>
                &nbsp;&nbsp;&nbsp;
                <Button type="primary" onClick={viewAll} className="custom-button">
                  <i className="fa fa-paper-plane" aria-hidden="true"></i>
                  &nbsp;&nbsp;&nbsp;&nbsp;View All
                </Button>
              </div>

              {loading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Oval
                    height={40}
                    width={40}
                    color="#4fa94d"
                    wrapperStyle={{}}
                    wrapperClass=""
                    visible={true}
                    ariaLabel="oval-loading"
                    secondaryColor="#4fa94d"
                    strokeWidth={2}
                    strokeWidthSecondary={2}
                  />
                </div>
              ) : attendanceData ? (
                hasData ? (
                  <Chart
                    chartType="PieChart"
                    data={chartData}
                    options={{
                      pieHole: 0.4,
                      pieSliceText: "value",
                      tooltip: { text: "value" },
                      colors: ["green", "#faae20", "#FF5733", "#3366CC"],
                      legend: {
                        position: "labeled",
                      },
                    }}
                    style={{ marginTop: "-10px" }}
                    width={"100%"}
                    height={"90%"}
                  />
                ) : (
                  <Empty description="No attendance data available for the selected department." />
                )
              ) : selectedDepartment === "All Departments" ? (
                <Empty description="Please select a specific department to view attendance data." />
              ) : (
                <Empty description="No attendance data available yet!" />
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

export default Attendance;
